import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useEditorStore } from "@/stores/editor/editor-store";
import { Component } from "@/types";

interface TouchOptimizationContextValue {
  isTouchDevice: boolean;
  isLongPressing: boolean;
  touchScale: number;
  handleTouchStart: (e: React.TouchEvent, componentId?: string) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: (e: React.TouchEvent) => void;
  getTouchHitbox: () => number;
}

const TouchOptimizationContext =
  createContext<TouchOptimizationContextValue | null>(null);

export const useTouchOptimization = () => {
  const context = useContext(TouchOptimizationContext);
  if (!context) {
    throw new Error(
      "useTouchOptimization must be used within TouchOptimizationProvider",
    );
  }
  return context;
};

interface TouchOptimizationProviderProps {
  children: React.ReactNode;
}

export const TouchOptimizationProvider: React.FC<
  TouchOptimizationProviderProps
> = ({ children }) => {
  const { viewport, zoom, selectComponent, setHoveredComponent } =
    useEditorStore();

  const [isTouchDevice, setIsTouchDevice] = React.useState(false);
  const [isLongPressing, setIsLongPressing] = React.useState(false);
  const [touchScale, setTouchScale] = React.useState(1);

  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const lastTouchTime = useRef<number>(0);
  const touchStartPosition = useRef<{ x: number; y: number } | null>(null);
  const currentTouchTarget = useRef<string | null>(null);

  // Detect touch device capability
  useEffect(() => {
    const checkTouchSupport = () => {
      setIsTouchDevice(
        "ontouchstart" in window ||
          navigator.maxTouchPoints > 0 ||
          // Legacy IE/Edge vendor property, absent from the standard Navigator type.
          ((navigator as Navigator & { msMaxTouchPoints?: number }).msMaxTouchPoints ?? 0) > 0,
      );
    };

    checkTouchSupport();
    window.addEventListener("resize", checkTouchSupport);

    return () => window.removeEventListener("resize", checkTouchSupport);
  }, []);

  // Calculate appropriate touch scale based on viewport
  useEffect(() => {
    const calculateTouchScale = () => {
      let scale = 1;

      // Increase touch targets on mobile viewports
      if (viewport === "mobile") {
        scale = 1.5;
      } else if (viewport === "tablet") {
        scale = 1.25;
      }

      // Adjust for zoom level - smaller targets when zoomed in
      if (zoom > 1) {
        scale = scale / Math.sqrt(zoom);
      } else if (zoom < 1) {
        scale = scale * (2 - zoom); // Larger targets when zoomed out
      }

      setTouchScale(Math.max(0.8, Math.min(2, scale)));
    };

    calculateTouchScale();
  }, [viewport, zoom]);

  // Get minimum touch hit box size (44px is iOS recommendation)
  const getTouchHitbox = useCallback(() => {
    return Math.max(44, 44 * touchScale);
  }, [touchScale]);

  // Handle touch start with long press detection
  const handleTouchStart = useCallback(
    (e: React.TouchEvent, componentId?: string) => {
      if (!isTouchDevice) return;

      const touch = e.touches[0];
      const now = Date.now();

      touchStartPosition.current = { x: touch.clientX, y: touch.clientY };
      currentTouchTarget.current = componentId || null;
      lastTouchTime.current = now;

      // Start long press timer for context menu or selection
      longPressTimer.current = setTimeout(() => {
        setIsLongPressing(true);

        // Haptic feedback on supported devices
        if ("vibrate" in navigator) {
          navigator.vibrate(50);
        }

        // Long press selects component and shows context actions
        if (componentId) {
          selectComponent(componentId);
          // Could trigger context menu here
        }
      }, 500); // 500ms for long press

      // Prevent default to avoid text selection on long press
      e.preventDefault();
    },
    [isTouchDevice, selectComponent],
  );

  // Handle touch move with gesture recognition
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isTouchDevice || !touchStartPosition.current) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartPosition.current.x;
      const deltaY = touch.clientY - touchStartPosition.current.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Cancel long press if user moves too far
      if (distance > 10 && longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
        setIsLongPressing(false);
      }

      // Handle touch move for drag operations
      // Touch handling for AI-first component interaction
      e.preventDefault();
    },
    [isTouchDevice],
  );

  // Handle touch end with tap detection
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!isTouchDevice) return;

      const now = Date.now();
      const touchDuration = now - lastTouchTime.current;

      // Clear long press timer
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }

      // Handle different touch gestures
      if (!isLongPressing && touchDuration < 300) {
        // Quick tap - select component or clear selection
        if (currentTouchTarget.current) {
          selectComponent(currentTouchTarget.current);
        }
      }

      // Reset touch state
      setIsLongPressing(false);
      touchStartPosition.current = null;
      currentTouchTarget.current = null;

      e.preventDefault();
    },
    [isTouchDevice, isLongPressing, selectComponent],
  );

  // Enhanced touch event handlers with improved performance
  const enhancedHandlers = {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };

  // CSS custom properties for touch optimization
  useEffect(() => {
    const root = document.documentElement;

    if (isTouchDevice) {
      root.style.setProperty("--touch-target-size", `${getTouchHitbox()}px`);
      root.style.setProperty("--touch-scale", touchScale.toString());

      // Add touch-optimized class to body
      document.body.classList.add("touch-device");
    } else {
      document.body.classList.remove("touch-device");
    }

    return () => {
      document.body.classList.remove("touch-device");
    };
  }, [isTouchDevice, touchScale, getTouchHitbox]);

  // Performance optimization: Reduce update frequency on touch devices
  useEffect(() => {
    if (isTouchDevice) {
      // Throttle hover events on touch devices to improve performance
      const style = document.createElement("style");
      style.textContent = `
        .touch-device .group:hover,
        .touch-device [role="button"]:hover {
          transition-delay: 50ms;
        }

        .touch-device .canvas-container {
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }

        .touch-device .component-palette-list {
          -webkit-overflow-scrolling: touch;
        }

        /* Larger touch targets */
        .touch-device [role="button"],
        .touch-device .interactive-component {
          min-height: var(--touch-target-size);
          min-width: var(--touch-target-size);
          touch-action: manipulation;
        }

        /* Improved visual feedback for touch */
        .touch-device [role="button"]:active {
          transform: scale(0.95);
          opacity: 0.8;
        }

        /* Prevent text selection on touch */
        .touch-device .canvas-container,
        .touch-device .component-palette {
          -webkit-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
        }
      `;

      document.head.appendChild(style);

      return () => {
        if (style.parentNode) {
          style.parentNode.removeChild(style);
        }
      };
    }
    return undefined;
  }, [isTouchDevice]);

  const contextValue: TouchOptimizationContextValue = {
    isTouchDevice,
    isLongPressing,
    touchScale,
    ...enhancedHandlers,
    getTouchHitbox,
  };

  return (
    <TouchOptimizationContext.Provider value={contextValue}>
      {children}
    </TouchOptimizationContext.Provider>
  );
};

// Utility hook for responsive touch behaviors
export const useResponsiveTouch = () => {
  const { isTouchDevice, touchScale, getTouchHitbox } = useTouchOptimization();

  return {
    isTouchDevice,
    touchScale,
    getTouchHitbox,

    // Helper to get appropriate spacing for touch
    getTouchSpacing: useCallback(
      (baseSpacing: number) => {
        return isTouchDevice
          ? Math.max(baseSpacing, baseSpacing * touchScale)
          : baseSpacing;
      },
      [isTouchDevice, touchScale],
    ),

    // Helper to determine if component needs larger hit area
    needsLargerHitArea: useCallback(
      (component: Component) => {
        if (!isTouchDevice) return false;

        const minSize = getTouchHitbox();
        return (
          component.size.width < minSize || component.size.height < minSize
        );
      },
      [isTouchDevice, getTouchHitbox],
    ),
  };
};
